import { Many, relations } from "drizzle-orm"; // Import Many
import { activationCodes, products } from "./activation"; // Assuming products schema is in activation.ts
import { integer, jsonb, numeric, pgEnum, pgTable, primaryKey, text, timestamp } from "drizzle-orm/pg-core";

import { user } from "./auth"; // Assuming user schema is in auth.ts

// Enum for order status
export const orderStatusEnum = pgEnum("order_status", [
  "pending_payment",      // Order created, awaiting payment
  "paid",                 // Payment confirmed
  "manually_marked_paid", // Manually marked as paid by admin
  "cancelled",            // Order cancelled by user or admin
  "refunded",             // Order has been refunded
]);

// Enum for payment method
export const paymentMethodEnum = pgEnum("payment_method", [
  "stripe",
  "alipay",
  "wechatpay",
  "other",
]);

// Enum for order item status
export const orderItemStatusEnum = pgEnum("order_item_status", [
  "pending",           // Item is part of an order, awaiting fulfillment
  "fulfilled",         // Item has been delivered or access granted
  "cancelled",         // Item has been cancelled
  "refunded",          // Item has been refunded
  "license_generated", // Specific to digital goods where a license is created
]);

// Orders Table
export const orders = pgTable("orders", {
  id: text("id").primaryKey().$defaultFn(() => crypto.randomUUID()),
  orderNumber: text("order_number").notNull().unique(), // e.g., ORD-20240516-ABCDE
  userId: text("user_id").references(() => user.id, { onDelete: "set null" }), // Nullable for guest checkouts
  customerEmail: text("customer_email").notNull(),
  customerName: text("customer_name"),
  paymentSnapshot: jsonb("payment_snapshot").notNull(), // { amount, currency, methodId, transactionId, gatewayResponse }
  orderStatus: orderStatusEnum("order_status").default("pending_payment").notNull(),
  paymentMethod: paymentMethodEnum("payment_method").notNull(),
  shippingAddress: jsonb("shipping_address"), // Nullable, for physical products
  billingAddress: jsonb("billing_address"),   // Nullable
  totalAmount: numeric("total_amount").notNull(), // Total amount for the order
  currency: text("currency").notNull(),           // e.g., "USD", "EUR"
  notes: text("notes"), // Optional notes from customer or admin
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull().$onUpdate(() => new Date()),
});

export const ordersRelations = relations(orders, ({ one, many }) => ({
  user: one(user, {
    fields: [orders.userId],
    references: [user.id],
  }),
  orderItems: many(orderItems),
}));

// Order Items Table
export const orderItems = pgTable("order_items", {
  id: text("id").primaryKey().$defaultFn(() => crypto.randomUUID()),
  orderId: text("order_id").notNull().references(() => orders.id, { onDelete: "cascade" }),
  productId: text("product_id").notNull().references(() => products.id, { onDelete: "restrict" }), // Prevent deleting product if it's in an order
  quantity: integer("quantity").notNull().default(1),
  priceAtPurchase: numeric("price_at_purchase").notNull(), // Price per unit at the time of order
  productSnapshot: jsonb("product_snapshot").notNull(), // { name, version, keyFeatures } to preserve historical accuracy
  status: orderItemStatusEnum("status").default("pending").notNull(),
  // licenseKey: text("license_key"), // DEPRECATED: Replaced by linkage to activationCodes table
  fulfillmentDetails: jsonb("fulfillment_details"), // e.g., tracking number for physical, download link for digital
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull().$onUpdate(() => new Date()),
});

export const orderItemsRelations = relations(orderItems, ({ one, many }) => ({
  order: one(orders, {
    fields: [orderItems.orderId],
    references: [orders.id],
  }),
  product: one(products, {
    fields: [orderItems.productId],
    references: [products.id],
  }),
  activationCodes: many(activationCodes), // Link to multiple activation codes
}));